/** Tokens visuais — Central de Mensagens Klinvia (frontend-design + agendaTheme) */
import { AGENDA_THEME } from '../agendaTheme';

export const INBOX_THEME = {
  ...AGENDA_THEME,
  whatsapp: '#25D366',
  whatsappDark: '#128C7E',
  chatBgLight: '#F0FAF9',
  chatBgDark: '#0F172A',
  bubbleOutbound: '#DCF8C6',
  bubbleOutboundBorder: '#BBF7D0',
  bubbleOutboundShadow: '0 2px 4px rgba(13, 148, 136, 0.12)',
  bubbleInbound: '#FFFFFF',
  bubbleInboundBorder: '#E2E8F0',
  bubbleText: '#0F172A',
  bubbleTime: '#64748B',
  listSelectedBg: 'rgba(13, 148, 136, 0.08)',
  listSelectedBgDark: 'rgba(13, 148, 136, 0.18)',
  listHoverBg: '#F8FAFC',
  listHoverBgDark: 'rgba(255, 255, 255, 0.04)',
  listSelectedHoverBg: '#E6F7F5',
  listSelectedHoverBgDark: 'rgba(13, 148, 136, 0.22)',
  unreadBadge: '#25D366',
  /** Divisor entre conversas (estilo WhatsApp). */
  listRowDividerLight: '#e9edef',
  listRowDividerDark: 'rgba(255,255,255,0.08)',
  listPreviewLight: '#667781',
  listPreviewLightUnread: '#3b4a54',
  listPreviewDark: 'rgba(255,255,255,0.55)',
  listPreviewDarkUnread: 'rgba(255,255,255,0.82)',
  listTimeLight: '#667781',
  listTimeDark: 'rgba(255,255,255,0.45)',
  mobileHeaderGradient: `linear-gradient(135deg, ${AGENDA_THEME.tealDark} 0%, ${AGENDA_THEME.teal} 52%, #128C7E 100%)`,
  inputBg: '#F1F5F9',
  inputBgDark: 'rgba(255,255,255,0.06)',
  filePreviewBg: 'rgba(13, 148, 136, 0.1)',
  filePreviewBorder: 'rgba(13, 148, 136, 0.22)',
} as const;

export const inboxClinicalGridBg = {
  backgroundColor: INBOX_THEME.muted,
  backgroundImage: `
    linear-gradient(${INBOX_THEME.gridLine} 1px, transparent 1px),
    linear-gradient(90deg, ${INBOX_THEME.gridLine} 1px, transparent 1px)
  `,
  backgroundSize: '24px 24px',
};

export const inboxPageShellSx = {
  flex: 1,
  height: '100%',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column' as const,
  ...inboxClinicalGridBg,
  position: 'relative' as const,
  overflow: 'hidden',
};

export const inboxLoadingSkeletonSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 2,
  p: { xs: 2, md: 3 },
  minHeight: 320,
};

/** Largura da coluna de conversas no desktop (inbox dinâmico). */
export const INBOX_CONVERSATION_LIST_WIDTH_PX = 380;

export const inboxConversationListPaperSx = (mode: 'light' | 'dark') => ({
  flex: { xs: '1', md: `0 0 ${INBOX_CONVERSATION_LIST_WIDTH_PX}px` },
  minWidth: { xs: 'auto', md: INBOX_CONVERSATION_LIST_WIDTH_PX },
  height: '100%',
  minHeight: 0,
  overflow: 'hidden',
  borderRadius: 0,
  borderRight: { xs: 0, md: `1px solid ${INBOX_THEME.borderSoft}` },
  flexDirection: 'column' as const,
  backgroundColor: mode === 'dark' ? '#0b141a' : '#fff',
  boxShadow: 'none',
});

/** Superfície do cabeçalho da lista — reutilizada no cabeçalho do chat (mobile/desktop). */
export const inboxListHeaderSurfaceSx = (mode: 'light' | 'dark') => ({
  borderBottom: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.08)' : INBOX_THEME.borderSoft}`,
  bgcolor: mode === 'dark' ? '#252525' : INBOX_THEME.muted,
  boxShadow: mode === 'dark' ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 2px rgba(15, 23, 42, 0.04)',
  ...(mode === 'light'
    ? {
        backgroundImage: `
          linear-gradient(${INBOX_THEME.gridLine} 1px, transparent 1px),
          linear-gradient(90deg, ${INBOX_THEME.gridLine} 1px, transparent 1px)
        `,
        backgroundSize: '24px 24px',
      }
    : {}),
});

export const inboxListHeaderSx = (mode: 'light' | 'dark') => ({
  px: { xs: 1.5, md: 1.75 },
  pt: { xs: 'calc(env(safe-area-inset-top, 0px) + 16px)', md: 2 },
  pb: { xs: 1.5, md: 1.75 },
  flexShrink: 0,
  ...inboxListHeaderSurfaceSx(mode),
});

export const inboxChannelIconWrapSx = (mode: 'light' | 'dark') => ({
  p: 0.75,
  borderRadius: '12px',
  bgcolor: mode === 'dark' ? 'rgba(37, 211, 102, 0.15)' : INBOX_THEME.tealSoft,
  border: `1px solid ${mode === 'dark' ? 'rgba(37, 211, 102, 0.25)' : INBOX_THEME.border}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const inboxListTitleSx = {
  color: INBOX_THEME.navy,
  fontWeight: 700,
  fontSize: '1.05rem',
  letterSpacing: '-0.02em',
  flex: 1,
};

export const inboxSearchFieldSx = (mode: 'light' | 'dark') => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: mode === 'dark' ? INBOX_THEME.inputBgDark : INBOX_THEME.inputBg,
    '& fieldset': {
      borderColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : INBOX_THEME.borderSoft,
    },
    '&:hover fieldset': {
      borderColor: INBOX_THEME.teal,
    },
    '&.Mui-focused fieldset': {
      borderColor: INBOX_THEME.teal,
    },
    '&:hover': {
      bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : INBOX_THEME.mutedAlt,
    },
    '&.Mui-focused': {
      bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : INBOX_THEME.paper,
    },
  },
});

/** Área dos filtros da fila (status + atribuição) na lista de conversas. */
export const inboxQueueFiltersWrapSx = {
  px: 1.75,
  pb: 1,
  pt: 0.25,
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 0.25,
  flexShrink: 0,
};

export const inboxQueueFilterSectionLabelSx = (mode: 'light' | 'dark') => ({
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase' as const,
  color: mode === 'dark' ? 'rgba(255,255,255,0.42)' : INBOX_THEME.slateFaint,
  lineHeight: 1.2,
  px: 0.15,
});

export const inboxQueueFilterScrollRowSx = {
  display: 'flex',
  flexWrap: 'nowrap' as const,
  alignItems: 'center',
  gap: 0.5,
  overflowX: 'auto' as const,
  overflowY: 'hidden' as const,
  pb: 0.35,
  mx: -0.25,
  px: 0.25,
  WebkitOverflowScrolling: 'touch' as const,
  scrollbarWidth: 'none' as const,
  '&::-webkit-scrollbar': { display: 'none' },
};

export const inboxQueueFilterChipSx = (active: boolean, mode: 'light' | 'dark') => ({
  height: 28,
  flexShrink: 0,
  maxWidth: 'none',
  fontSize: '0.7rem',
  fontWeight: 600,
  borderRadius: 999,
  cursor: 'pointer',
  border: '1px solid',
  borderColor: active
    ? INBOX_THEME.teal
    : mode === 'dark'
      ? 'rgba(255,255,255,0.14)'
      : 'rgba(15,23,42,0.1)',
  bgcolor: active ? INBOX_THEME.teal : mode === 'dark' ? 'rgba(255,255,255,0.04)' : INBOX_THEME.paper,
  color: active ? '#fff' : mode === 'dark' ? 'rgba(255,255,255,0.78)' : INBOX_THEME.slate,
  boxShadow: active
    ? '0 1px 2px rgba(13, 148, 136, 0.22)'
    : mode === 'dark'
      ? 'none'
      : '0 1px 0 rgba(15, 23, 42, 0.04)',
  transition: 'background-color .15s ease, color .15s ease, border-color .15s ease, box-shadow .15s ease',
  '& .MuiChip-label': {
    px: 1.15,
    whiteSpace: 'nowrap' as const,
  },
  '&:hover': {
    bgcolor: active
      ? INBOX_THEME.tealDark
      : mode === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : INBOX_THEME.mutedAlt,
  },
  '&.Mui-disabled': {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
});

export const inboxConversationListSx = (mode: 'light' | 'dark') => ({
  py: 0,
  flex: 1,
  overflow: 'auto',
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(15, 23, 42, 0.15)',
    borderRadius: '3px',
    '&:hover': {
      background: mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(15, 23, 42, 0.25)',
    },
  },
});

export const inboxLoadMoreButtonSx = (mode: 'light' | 'dark') => ({
  borderRadius: 2,
  textTransform: 'none' as const,
  py: 1.25,
  borderColor: mode === 'dark' ? 'rgba(255,255,255,0.2)' : INBOX_THEME.borderSoft,
  color: INBOX_THEME.slateMid,
  fontWeight: 600,
  '&:hover': {
    borderColor: INBOX_THEME.teal,
    bgcolor: mode === 'dark' ? 'rgba(13, 148, 136, 0.12)' : INBOX_THEME.tealSoft,
    color: INBOX_THEME.tealDark,
  },
});

export const inboxChatHeaderSx = (mode: 'light' | 'dark' = 'light') => ({
  p: { xs: 1.25, sm: 1.5, md: 2 },
  borderRadius: 0,
  ...inboxListHeaderSurfaceSx(mode),
});

export const inboxChatAreaSx = (mode: 'light' | 'dark') =>
  mode === 'dark'
    ? {
        flex: 1,
        display: 'flex',
        flexDirection: 'column' as const,
        overflow: 'hidden',
        minHeight: 0,
        bgcolor: INBOX_THEME.chatBgDark,
        position: 'relative' as const,
      }
    : {
        flex: 1,
        display: 'flex',
        flexDirection: 'column' as const,
        overflow: 'hidden',
        minHeight: 0,
        bgcolor: INBOX_THEME.chatBgLight,
        backgroundImage: `
          linear-gradient(${INBOX_THEME.gridLine} 1px, transparent 1px),
          linear-gradient(90deg, ${INBOX_THEME.gridLine} 1px, transparent 1px),
          radial-gradient(circle at 18% 22%, rgba(13, 148, 136, 0.06) 0%, transparent 42%),
          radial-gradient(circle at 82% 78%, rgba(13, 148, 136, 0.05) 0%, transparent 40%)
        `,
        backgroundSize: '24px 24px, 24px 24px, auto, auto',
        position: 'relative' as const,
      };

export const inboxMobileChatHeaderSx = (mode: 'light' | 'dark' = 'light') => ({
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'stretch',
  gap: 0.25,
  px: 2,
  pt: 'calc(env(safe-area-inset-top, 0px) + 14px)',
  pb: 0.75,
  color: mode === 'dark' ? '#fff' : INBOX_THEME.navy,
  position: 'relative' as const,
  zIndex: 1,
  flexShrink: 0,
  ...inboxListHeaderSurfaceSx(mode),
});

/** Scroll da timeline no mobile — fundo sólido, sem vinheta no overscroll (iOS). */
export const inboxMobileMessagesScrollSx = {
  flex: 1,
  px: 1,
  py: 0.75,
  overflow: 'auto' as const,
  WebkitOverflowScrolling: 'touch' as const,
  overscrollBehavior: 'contain' as const,
  bgcolor: INBOX_THEME.chatBgLight,
  backgroundImage: 'none',
};

export const inboxMobileBackButtonSx = (mode: 'light' | 'dark' = 'light') =>
  mode === 'dark'
    ? {
        mr: 2,
        color: '#fff',
        bgcolor: 'rgba(255,255,255,0.12)',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
      }
    : {
        mr: 2,
        color: INBOX_THEME.navy,
        bgcolor: INBOX_THEME.paper,
        border: `1px solid ${INBOX_THEME.borderSoft}`,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        '&:hover': { bgcolor: INBOX_THEME.tealSoft, borderColor: INBOX_THEME.teal },
      };

/** Ícones de ação no cabeçalho mobile do chat. */
export const inboxMobileChatActionIconSx = (mode: 'light' | 'dark' = 'light') =>
  mode === 'dark'
    ? {
        color: '#fff',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
      }
    : {
        color: INBOX_THEME.slateMid,
        '&:hover': { color: INBOX_THEME.tealDark, bgcolor: INBOX_THEME.tealSoft },
      };

export const inboxMobileChatTagsRowSx = (mode: 'light' | 'dark' = 'light') => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0.25,
  flexShrink: 0,
  flexWrap: 'nowrap' as const,
  ...(mode === 'dark'
    ? {
        '& .MuiIconButton-root': { color: '#fff' },
        '& .MuiChip-root': {
          bgcolor: 'rgba(255,255,255,0.15)',
          color: '#fff',
        },
      }
    : {
        '& .MuiIconButton-root': { color: INBOX_THEME.slateMid },
      }),
});

/** Faixa de ícones de ação no cabeçalho mobile — uma linha, da borda esquerda, scroll horizontal */
export const inboxMobileChatActionsSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexWrap: 'nowrap',
  gap: 0.25,
  alignSelf: 'stretch',
  mx: -2,
  px: 0.5,
  minWidth: 0,
  overflowX: 'auto',
  overflowY: 'hidden',
  touchAction: 'pan-x pan-y',
  WebkitOverflowScrolling: 'touch',
  overscrollBehaviorX: 'contain',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  '& > *': { flexShrink: 0 },
};

export const inboxActionIconSx = {
  color: INBOX_THEME.tealBright,
  '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.15)' },
};

export const inboxHealthcareIconSx = {
  color: INBOX_THEME.tealBright,
  '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.15)' },
};

export const inboxDesktopHealthcareIconSx = {
  color: INBOX_THEME.teal,
  '&:hover': { color: INBOX_THEME.tealDark, bgcolor: INBOX_THEME.tealSoft },
};

export const inboxTemplateIconSx = {
  color: INBOX_THEME.teal,
  '&:hover': { color: INBOX_THEME.tealDark, bgcolor: INBOX_THEME.tealSoft },
};

/** Teto horizontal da bolha (% da linha do chat), estilo WhatsApp. */
export const inboxMessageBubbleShellSx = {
  maxWidth: { xs: '80%', sm: '75%', md: '70%' },
  width: 'fit-content',
  minWidth: 0,
  flex: '0 1 auto',
  boxSizing: 'border-box' as const,
  display: 'flex',
  gap: 0.75,
};

/** Propaga o teto do shell até o Paper (largura intrínseca no mobile, estilo WhatsApp). */
export const inboxMessageBubbleBodyHostSx = {
  minWidth: 0,
  maxWidth: '100%',
  width: { xs: 'auto', md: '100%' },
  position: 'relative' as const,
};

/** @deprecated Use inboxMessageBubbleShellSx + layout row/column no componente. */
export const inboxMessageBubbleWrapSx = {
  ...inboxMessageBubbleShellSx,
  alignItems: 'center',
};

export const inboxMessageBubbleSx = (
  direction: 'inbound' | 'outbound',
  mode: 'light' | 'dark',
) => ({
  p: { xs: '5px 7px 3px 9px', sm: '8px 10px 5px 12px', md: '9px 12px 6px 14px' },
  width: { xs: 'auto', md: '100%' },
  minWidth: 0,
  maxWidth: '100%',
  display: { xs: 'inline-block', md: 'block' },
  boxSizing: 'border-box' as const,
  overflowWrap: 'anywhere' as const,
  wordBreak: 'break-word' as const,
  bgcolor:
    direction === 'outbound'
      ? INBOX_THEME.bubbleOutbound
      : mode === 'dark'
        ? 'background.paper'
        : INBOX_THEME.bubbleInbound,
  color: INBOX_THEME.bubbleText,
  borderRadius:
    direction === 'outbound'
      ? { xs: '14px 14px 4px 14px', md: '18px 18px 4px 18px' }
      : { xs: '14px 14px 14px 4px', md: '18px 18px 18px 4px' },
  boxShadow:
    direction === 'outbound'
      ? INBOX_THEME.bubbleOutboundShadow
      : mode === 'dark'
        ? '0 2px 4px rgba(0,0,0,0.2)'
        : '0 2px 4px rgba(15, 23, 42, 0.08)',
  border: '1px solid',
  borderColor:
    direction === 'outbound'
      ? INBOX_THEME.bubbleOutboundBorder
      : mode === 'dark'
        ? 'divider'
        : INBOX_THEME.bubbleInboundBorder,
});

export const inboxComposerPaperSx = {
  p: 2,
  borderRadius: 0,
  borderTop: `1px solid ${INBOX_THEME.borderSoft}`,
  mt: 'auto',
  bgcolor: INBOX_THEME.paper,
  boxShadow: '0 -1px 3px rgba(15, 23, 42, 0.06)',
};

export const inboxFilePreviewSx = {
  mb: 1,
  p: 1,
  bgcolor: INBOX_THEME.filePreviewBg,
  border: `1px solid ${INBOX_THEME.filePreviewBorder}`,
  borderRadius: 1.5,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

export const inboxMessageInputSx = (mode: 'light' | 'dark') => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '20px',
    bgcolor: mode === 'dark' ? INBOX_THEME.inputBgDark : INBOX_THEME.inputBg,
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: INBOX_THEME.borderSoft },
    '&.Mui-focused fieldset': { borderColor: INBOX_THEME.teal },
  },
  '& .MuiInputBase-inputMultiline': {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    lineHeight: 1.45,
  },
});

export const inboxEmptyStateSx = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  ...inboxClinicalGridBg,
};

export const inboxEmptyStateTextSx = {
  color: INBOX_THEME.slate,
  fontWeight: 600,
  letterSpacing: '-0.01em',
};

export const inboxTemplateDialogTitleIconSx = {
  color: INBOX_THEME.teal,
};

export const inboxTemplateCardSx = {
  p: 1.5,
  cursor: 'pointer',
  borderRadius: 2,
  transition: INBOX_THEME.transition,
  '&:hover': {
    borderColor: INBOX_THEME.teal,
    bgcolor: INBOX_THEME.tealSoft,
  },
};

export const inboxGalleryActionIconSx = {
  color: INBOX_THEME.tealBright,
  p: 0.5,
  '&:hover': { color: INBOX_THEME.teal, bgcolor: 'rgba(13, 148, 136, 0.15)' },
};

export const inboxDocActionIconSx = {
  color: INBOX_THEME.teal,
  '&:hover': { color: INBOX_THEME.tealDark },
};

export function inboxListRowDividerColor(mode: 'light' | 'dark'): string {
  return mode === 'dark' ? INBOX_THEME.listRowDividerDark : INBOX_THEME.listRowDividerLight;
}

export function inboxListPreviewColor(mode: 'light' | 'dark', hasUnread: boolean): string {
  if (mode === 'dark') {
    return hasUnread ? INBOX_THEME.listPreviewDarkUnread : INBOX_THEME.listPreviewDark;
  }
  return hasUnread ? INBOX_THEME.listPreviewLightUnread : INBOX_THEME.listPreviewLight;
}

export function inboxListTimeColor(mode: 'light' | 'dark', hasUnread: boolean): string {
  if (hasUnread) return INBOX_THEME.unreadBadge;
  return mode === 'dark' ? INBOX_THEME.listTimeDark : INBOX_THEME.listTimeLight;
}

/** Linha da lista — layout plano estilo WhatsApp (sem cards). */
export function inboxListItemSx(opts: {
  selected: boolean;
  mode: 'light' | 'dark';
}) {
  const { selected, mode } = opts;
  const isDark = mode === 'dark';
  return {
    cursor: 'pointer',
    px: 0,
    py: 0,
    mx: 0,
    my: 0,
    borderRadius: 0,
    alignItems: 'stretch',
    backgroundColor: selected
      ? isDark
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(0, 0, 0, 0.04)'
      : 'transparent',
    borderLeft: 'none',
    transition: 'background-color 0.12s ease',
    '&:hover': {
      backgroundColor: selected
        ? isDark
          ? 'rgba(255, 255, 255, 0.08)'
          : 'rgba(0, 0, 0, 0.06)'
        : isDark
          ? INBOX_THEME.listHoverBgDark
          : INBOX_THEME.listHoverBg,
    },
  };
}

export const inboxPrimaryIconButtonSx = {
  color: INBOX_THEME.teal,
  '&:hover': { bgcolor: INBOX_THEME.tealSoft, color: INBOX_THEME.tealDark },
  '&.Mui-disabled': { color: INBOX_THEME.slateFaint },
};

export const inboxUnreadTimeSx = {
  color: INBOX_THEME.unreadBadge,
  fontSize: '0.75rem',
  fontWeight: 400,
  flexShrink: 0,
  lineHeight: 1.2,
};
